import { Component } from 'react';

import loadable from '@loadable/component';

import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Snackbar from '@material-ui/core/Snackbar';

// required for Dialog
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';

import {
  BrowserRouter as Router, Route, Switch,
} from 'react-router-dom';
import ScrollMemory from 'react-router-scroll-memory';

import { LightGreen, Loading, MediumGreen } from './components/Styles';
import Home from './containers/Home';
import LogOut from './containers/LogOut';
import Recovery from './containers/Recovery';
import SignIn from './containers/SignIn';

import ApiDirector from './components/ApiDirector';
import AppBar from './components/AppBar';
import NavDrawer from './components/NavDrawer';

import Vault from './helpers/Vault';

import Notifications from './notifications';

/**
 * We're going to be spreading a lot of props in this file.
 * It's a reasonably terse and DRY way of writing routing code given how this
 * app does prop passing. Using the Context API could make this go away, but
 * that's a much more substantial change.
 */
/* eslint-disable react/jsx-props-no-spreading */

/* dynamic splits */
const VendorHome = loadable(() => import('./containers/VendorHome.js'), {
  fallback: <Loading>loading vendor tools</Loading>,
});
const NewProduct = loadable(() => import('./containers/NewProduct.js'), {
  fallback: <Loading>loading vendor tools</Loading>,
});
const EditProduct = loadable(() => import('./containers/EditProduct.js'), {
  fallback: <Loading>loading vendor tools</Loading>,
});
const LedgerSelector = loadable(() => import('./containers/LedgerSelector.js'), {
  fallback: <Loading>loading ledger tools</Loading>,
});

const CustomerHome = loadable(() => import('./containers/CustomerHome.js'), {
  fallback: <Loading>getting ready to shop</Loading>,
});

const ShopperHome = loadable(() => import('./containers/ShopperHome.js'), {
  fallback: <Loading>loading shopper tools</Loading>,
});

const MarketManager = loadable(() => import('./containers/MarketManager.js'), {
  fallback: <Loading>loading market manager tools</Loading>,
});
const NewMarketEvent = loadable(() => import('./containers/NewMarketEvent.js'), {
  fallback: <Loading>loading market manager tools</Loading>,
});
const EditMarketEvent = loadable(() => import('./containers/EditMarketEvent.js'), {
  fallback: <Loading>loading market manager tools</Loading>,
});

const MarketSearch = loadable(() => import('./containers/MarketSearch.js'), {
  fallback: <Loading>loading search engine</Loading>,
});

const Accounts = loadable(() => import('./containers/Accounts.js'), {
  fallback: <Loading>loading accounts</Loading>,
});

const Orders = loadable(() => import('./containers/Orders.js'), {
  fallback: <Loading>loading orders</Loading>,
});

const Checkout = loadable(() => import('./containers/Checkout.js'), {
  fallback: <Loading>loading checkout</Loading>,
});

const GiftCards = loadable(() => import('./containers/GiftCards.js'), {
  fallback: <Loading>loading gift cards</Loading>,
});

const GiftCardThanks = loadable(() => import('./containers/GiftCardThanks.js'), {
  fallback: <Loading>loading gift cards</Loading>,
});

const RecentOrders = loadable(() => import('./containers/RecentOrders.js'), {
  fallback: <Loading>loading recent orders</Loading>,
});

const NotificationPreferences = loadable(() => import('./containers/NotificationPreferences.js'), {
  fallback: <Loading>loading preferences</Loading>,
});

const MarketDetail = loadable(() => import('./containers/MarketDetail.js'), {
  fallback: <Loading>loading market viewer</Loading>,
});
const ProductDetail = loadable(() => import('./containers/ProductDetail.js'), {
  fallback: <Loading>loading product viewer</Loading>,
});
const VendorDetail = loadable(() => import('./containers/VendorDetail.js'), {
  fallback: <Loading>loading vendor viewer</Loading>,
});

const About = loadable(() => import('./containers/About.js'), {
  fallback: <Loading>hey, thanks for asking</Loading>,
});
const SignUp = loadable(() => import('./containers/SignUp.js'), {
  fallback: <Loading>let&apos;s sign you up</Loading>,
});

const DirectEvent = loadable(() => import('./containers/DirectEvent.js'), {
  fallback: <Loading>what&apos;s happening today</Loading>,
});

const DirectProduct = loadable(() => import('./containers/DirectProduct.js'), {
  fallback: <Loading>let&apos;s take a closer look</Loading>,
});

const DirectLandingPage = loadable(() => import('./containers/DirectLandingPage.js'), {
  fallback: <Loading>now taking preorders on Market 2day</Loading>,
});

// const MyLocations = loadable(() => import('./containers/MyLocations.js'), {
//   fallback: <Loading>listing your locations</Loading>,
// });

const DirectLocations = loadable(() => import('./containers/DirectLocations.js'), {
  fallback: <Loading>loading your locations</Loading>,
});

const EditDirectEvent = loadable(() => import('./containers/EditDirectEvent.js'), {
  fallback: <Loading>loading location details</Loading>,
});

const ShopDirectEvent = loadable(() => import('./containers/ShopDirectEvent.js'), {
  fallback: <Loading>loading location details</Loading>,
});

const DirectLocationDetail = loadable(() => import('./containers/DirectLocationDetail.js'), {
  fallback: <Loading>loading location details</Loading>,
});

const EditLocation = loadable(() => import('./containers/EditLocation.js'), {
  fallback: <Loading>loading location details</Loading>,
});

/**
 * NOTE: As of 2015-11-09 react-transform does not support a functional
 * component as the base compoenent that's passed to ReactDOM.render, so we
 * still use createClass here.
 */
// eslint-disable-next-line import/prefer-default-export
class App extends Component {
  constructor(props) {
    super(props);
    this.notifications = new Notifications({
      openDialog: this.openDialog,
    });
    this.state = {
      open: false,
      notices: {},
      loading: false,
      recovery: false,
      ready: false,
      dialog: {
        open: false,
        onClose: () => {},
        onAccept: () => {},
        title: '',
        description: '',
        negativeText: '',
        positionText: '',
      },
    };
  }

  componentDidMount = () => {
    if (document) {
      document.addEventListener('message', this.handleContainerMessage, false);
    }
    window.showLoading = this.showLoading;
    window.hideLoading = this.hideLoading;
    ApiDirector.fetch('system')
      .then((response) => response.json()
        .then((json) => {
          if (json) {
            this.setState({
              productCategories: json.product_categories,
              productStatuses: json.product_statuses,
              notices: json.notices,
              ready: true,
            });
          } else {
            // show the error somehow
          }
        }));
  }

  componentDidCatch(error, info) {
    // Display fallback UI
    this.setState({
      recovery: {
        error,
        info,
        setAppState: this.setAppState,
      },
    });
  }

  hideLoading = () => {
    const { loading } = this.state;
    if (loading) {
      this.setState({ loading: false });
    }
  }

  showLoading = () => {
    const { loading } = this.state;
    if (!loading) {
      this.setState({ loading: true });
    }
  }

  setAuthToken = (token, callback) => {
    const { setUser } = this.props;
    const handleProfileError = (e) => {
      this.handleLogout(() => {
        callback(e);
      });
    };

    ApiDirector.setAuthToken(token);
    Vault.get(Vault.keys.USER_ID)
      .then((userId) => {
        ApiDirector.fetch(
          `user_profiles/${userId}`,
          { blocking: false },
        )
          .then((profileResponse) => {
            try {
              profileResponse.json()
                .then((profileJson) => {
                  const update = this.reloadProfileUser(profileJson);
                  setUser(update.user);
                  this.updateDevices(profileJson);
                  this.setState({ ...update, errors: null }, () => callback());
                }, handleProfileError);
            } catch (e) {
              handleProfileError(e);
            }
          })
          .catch(handleProfileError);
      }, handleProfileError);
  }

  setAppState = (options, callback) => {
    Console.log('setAppState is deprecated. Please use AppContext to access application or session state, or indicate the necessary state change via the URL (route). Received:', options, 'from', new Error());
    this.setState(options, callback);
  }

  openDialog = ({
    title,
    description,
    positiveText,
    negativeText,
    onAccept,
  }) => {
    // If dialog is already open, do not change anything
    // We are running in a single instance pattern for dialogs.
    this.setState(({ dialog }) => (dialog.open ? null : ({
      dialog: {
        ...dialog,
        open: true,
        title,
        description,
        positiveText,
        negativeText,
        onAccept,
      },
    })));
  }

  // update devices is called with a valid profile
  // this assumes if a device token is available, it will be loaded by now.
  updateDevices = (profile) => {
    const token = this.notifications.getToken();
    if (token) {
      // With auth token set and a valid profile
      const { user_devices: devices = [] } = profile;
      // FIXME this is part of our token problem - needs to detect mobile app
      const os = 'web';
      const device = devices.find((d) => d.token === token && d.os === os);
      if (!device) {
        // if we don't know about the device put it in the user profile on the server.
        ApiDirector.fetch(`user_profiles/${profile.email}`, {
          method: 'PATCH',
          body: JSON.stringify({
            user_profile: {
              user_devices_attributes: [{
                token,
                os,
              }],
            },
          }),
        });
      }
    }
  }

  handleContainerMessage = (event) => {
    const message = JSON.parse(event.data);
    this.messageListeners.forEach((l) => {
      if (l.handlerName === message.handler) {
        l.handler(message.data);
      }
    });
  }

  handleLogin = (user, password, then) => {
    const { setUser } = this.props;
    this.setState({ errors: undefined });
    const formData = new FormData();
    formData.append('user[email]', user);
    formData.append('user[password]', password);
    formData.append('return_token', 1);
    ApiDirector.fetch('users/sign_in', {
      uriBase: '/',
      method: 'POST',
      redirect: 'manual',
      credentials: 'omit',
      body: formData,
    }, { })
      .then((response) => response.json()
        .then((json) => {
          if (json.status === 'success') {
            let authToken = 'managed';
            if (response.headers.get('authorization')) {
              authToken = response.headers.get('authorization');
            }
            ApiDirector.setAuthToken(authToken);
            ApiDirector.fetch(`user_profiles/${user}`)
              .then((profileResponse) => profileResponse.json()
                .then((profileJson) => {
                  setUser(profileJson);
                  this.setState({ errors: null });
                  Vault.store(Vault.keys.USER_ID, user);
                  then();
                }));
          } else {
            this.setState({ errors: { password: 'Invalid username/password' } });
          }
        }));
  }

  handleForgot = (user) => {
    ApiDirector.fetch(`forgot_password/${user}`, { method: 'POST' })
      .then((response) => response.json()
        .then(() => {
          this.setState({
            snackbarOpen: true,
            snackbarMessage: 'Check your email for a password recovery message.',
          });
        }));
  }

  reloadProfileUser = (user) => {
    const { vendor, market, customer } = this.state;
    if (user) {
      if (!user.error) {
        const update = {
          user,
        };
        if (vendor) {
          user.vendors.forEach((reloadedVendor) => {
            if (reloadedVendor.id === vendor.id) {
              update.vendor = reloadedVendor;
            }
          });
        }
        if (market) {
          user.managed_markets.forEach((reloadedMarket) => {
            if (reloadedMarket.id === market.id) {
              update.market = reloadedMarket;
            }
          });
        }
        if (customer) {
          update.customer = user.customer;
        }
        if (user.customer) {
          const { favorite_markets: fms } = user.customer;
          const anyOpenMarkets = fms
            .filter(({ market_events: me = [] }) => me.length > 0)
            .length > 0;
          if (anyOpenMarkets) {
            this.notifications.scheduleMultipleMarketFavoriteNotifications(fms);
          }
        }
        if (user.notification_prefs) {
          this.notifications.setPrefs(user.notification_prefs);
        }
        return update;
      }
    }
    return undefined;
  }

  reloadProfile = () => {
    const { user, setUser } = this.props;
    if (!user) return;
    ApiDirector.fetch(`user_profiles/${user.email}`)
      .then((response) => response.json()
        .then((userResponse) => {
          const update = this.reloadProfileUser(userResponse);
          if (update) {
            setUser(update.user);
            this.setState(update);
          }
        }));
  }

  patchOrder = (order) => {
    Console.log('patch order', order);
    const { user, setUser } = this.props;
    const { orders } = user;
    Console.log(orders);
    setUser({
      ...user,
      orders: [
        order,
        ...orders.filter((o) => (o.id !== order.id)),
      ].flat(),
    });
  }

  handleLogout = (cb) => {
    ApiDirector.setAuthToken(null);
    this.setState({ open: false }, () => {
      if (cb) {
        cb();
      }
    });
  }

  snackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    this.setState({ snackbarOpen: false });
  };

  handleMenu = () => {
    const { open } = this.state;
    this.setState({ open: !open });
  };

  handleClose = () => {
    this.setState({ open: false });
  };

  expandAppBar = (component) => {
    this.setState({ expandedAppBar: component });
  }

  render() {
    const {
      errors,
      expandedAppBar,
      dialog,
      notices,
      open,
      orders,
      productCategories,
      productStatuses,
      snackbarOpen,
      snackbarClose,
      snackbarMessage,
      ready,
      recovery,
    } = this.state;
    const basicProps = {
      setAppState: this.setAppState,
      setAuthToken: this.setAuthToken,
      reloadProfile: this.reloadProfile,
      patchOrder: this.patchOrder,
      expandAppBar: this.expandAppBar,
    };
    this.notifications.setNotices(notices);
    const systemProps = {
      notices,
      productCategories,
      productStatuses,
      notifications: this.notifications,
    };
    const loginProps = {
      errors,
      forgot: this.handleForgot,
      login: this.handleLogin,
      logout: this.handleLogout,
      signup: this.handleSignup,
    };
    const theme = createMuiTheme({
      typography: {
        fontFamily: '"Open Sans",sans-serif',
        fontSize: 17,
      },
      palette: {
        primary: {
          light: '#cccccc',
          main: MediumGreen,
          dark: '#333333',
          contrastText: LightGreen,
        },
      },
    });
    if (recovery) {
      return (
        <Router>
          <MuiThemeProvider theme={theme}>
            <Route
              render={(props) => (
                <Recovery
                  {...props}
                  {...recovery}
                />
              )}
            />
          </MuiThemeProvider>
        </Router>
      );
    }

    const router = (
      <Router>
        <MuiThemeProvider theme={theme}>
          <AppBar
            expandAppBar={this.expandAppBar}
            expandedAppBar={expandedAppBar}
            handleMenu={this.handleMenu}
          />
          <NavDrawer
            open={open}
            orders={orders}
            close={this.handleClose}
            notifications={this.notifications}
          />
          <Snackbar
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={this.snackbarClose}
            SnackbarContentProps={{
              'aria-describedby': 'message-id',
            }}
            message={<span id="message-id">{snackbarMessage}</span>}
            action={[
              <IconButton
                key="close"
                aria-label="Close"
                onClick={snackbarClose}
              >
                <CloseIcon />
              </IconButton>,
            ]}
          />
          <Dialog
            open={dialog.open}
            onClose={dialog.onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">{dialog.title}</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                {dialog.description}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  dialog.onAccept();
                  this.setState({
                    dialog: {
                      ...dialog,
                      open: false,
                    },
                  });
                }}
                color="primary"
                autoFocus
              >
                {dialog.positiveText}
              </Button>
              <Button
                onClick={() => {
                  this.setState({
                    dialog: {
                      ...dialog,
                      open: false,
                    },
                  });
                }}
                color="primary"
              >
                {dialog.negativeText}
              </Button>
            </DialogActions>
          </Dialog>
          <div>
            <ScrollMemory />
            <Switch>
              <Route
                path="/signin"
                render={(props) => (
                  <SignIn
                    {...props}
                    {...basicProps}
                    {...loginProps}
                  />
                )}
              />
              <Route
                path="/signup"
                render={(props) => (
                  <SignUp
                    {...props}
                    {...basicProps}
                    {...systemProps}
                    {...loginProps}
                  />
                )}
              />
              <Route
                path="/logout"
                render={(props) => (
                  <LogOut
                    {...props}
                    {...loginProps}
                  />
                )}
              />
              <Route
                path="/about"
                render={(props) => (
                  <About
                    {...props}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/vendor/:vendorId/admin/products/new"
                render={(props) => (
                  <NewProduct
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/vendor/:vendorId/admin/products/:productId"
                render={(props) => (
                  <EditProduct
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/vendor/:vendorId/admin/:tab"
                render={(props) => (
                  <VendorHome
                    {...props}
                    {...basicProps}
                  />
                )}
              />
              <Route
                path="/customer-home"
                render={(props) => (
                  <CustomerHome
                    {...props}
                    {...basicProps}
                  />
                )}
              />
              <Route
                path="/shopper-home/:marketId"
                render={(props) => (
                  <ShopperHome
                    {...props}
                    {...basicProps}
                  />
                )}
              />
              <Route
                path="/market-manager/:marketId"
                render={(props) => (
                  <MarketManager
                    {...props}
                    {...basicProps}
                  />
                )}
              />
              <Route
                path="/market-search"
                render={(props) => (
                  <MarketSearch
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/new-market-event/:marketId"
                render={(props) => (
                  <NewMarketEvent
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/orders"
                render={(props) => (
                  <Orders
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/recent_orders"
                render={(props) => (
                  <RecentOrders
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/gift_cards/thanks"
                render={(props) => (
                  <GiftCardThanks
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/gift_cards"
                render={(props) => (
                  <GiftCards
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/ledger"
                render={(props) => (
                  <LedgerSelector
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/checkout/:orderId"
                render={(props) => (
                  <Checkout
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/market/:marketId/vendor/:vendorId/product/:productId"
                render={(props) => (
                  <ProductDetail
                    {...props}
                    {...basicProps}
                  />
                )}
              />
              <Route
                path="/market/:marketId/vendor/:vendorId"
                render={(props) => (
                  <VendorDetail
                    {...props}
                    {...basicProps}
                  />
                )}
              />
              <Route
                path="/market/:marketId"
                render={(props) => (
                  <MarketDetail
                    {...props}
                    {...basicProps}
                  />
                )}
              />
              <Route
                path="/edit-market-event/:marketEventId"
                render={(props) => (
                  <EditMarketEvent
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/notification-preferences"
                render={(props) => (
                  <NotificationPreferences
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              {/* <Route
                path="/my-locations"
                render={(props) => (
                  <MyLocations
                    {...props}
                    {...basicProps}
                  />
                )}
              /> */}
              <Route
                path="/accounts"
                render={(props) => (
                  <Accounts
                    {...props}
                    {...basicProps}
                  />
                )}
              />
              <Route
                path="/direct/:slug/product/:productId"
                render={(props) => (
                  <DirectProduct
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/direct/:slug/event/:eventId"
                render={(props) => (
                  <DirectEvent
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/direct/:slug/admin/locations/new"
                render={(props) => (
                  <EditLocation
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/direct/:slug/admin/locations/:locationId/edit"
                render={(props) => (
                  <EditLocation
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/direct/:slug/admin/locations/:locationId/events/:eventId/edit"
                render={(props) => (
                  <EditDirectEvent
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/direct/:slug/admin/locations/:locationId/events/new"
                render={(props) => (
                  <EditDirectEvent
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/direct/:slug/admin/locations/:locationId/events/:eventId"
                render={(props) => (
                  <ShopDirectEvent
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/direct/:slug/admin/locations/:locationId"
                render={(props) => (
                  <DirectLocationDetail
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/direct/:slug/admin/locations"
                render={(props) => (
                  <DirectLocations
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/direct/:slug/:tab"
                render={(props) => (
                  <DirectLandingPage
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/direct/:slug"
                render={(props) => (
                  <DirectLandingPage
                    {...props}
                    {...basicProps}
                    {...systemProps}
                  />
                )}
              />
              <Route
                path="/"
                render={(props) => (
                  <Home
                    {...props}
                    {...basicProps}
                  />
                )}
              />
            </Switch>
          </div>
        </MuiThemeProvider>
      </Router>
    );

    /* This code is a bridge between the current setAppState passed prop and
     * using context for application-wide state. Expand it to capture more
     * app state in context, eventually removing setAppState completely.
     */
    return (
      <>
        { ready && router }
      </>
    );
  }
}

export default App;
